import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const MOBILE_MQ = "(max-width: 767px)";

/**
 * En mobile, redirige el click de cada input del buscador a una modal compartida
 * y activa la sección correspondiente. En desktop mantiene el popup (`core-popup`).
 */
export class FlySearchMobileInputModal implements Plugin {
  private clickHandler: ((event: MouseEvent) => void) | null = null;
  private mqHandler: ((event: MediaQueryListEvent) => void) | null = null;
  private mobileMq: MediaQueryList | null = null;
  private innerInput: HTMLInputElement | null = null;
  private static readonly SECTION_SELECTOR = "[data-shared-modal-section]";
  private static readonly FOOTER_SELECTOR = "[data-shared-modal-footer]";
  private static readonly TITLE_SELECTOR = ".com-modalB2C__title";
  private static readonly sectionTitles: Record<string, string> = {
    origin: "Selecciona un origen",
    destination: "Selecciona un destino",
    dates: "Selecciona fecha",
    passengers: "Selecciona número de pasajeros",
  };

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.innerInput = this.element.querySelector<HTMLInputElement>("input");

    this.clickHandler = (event: MouseEvent) => {
      if (!this.isMobile()) return;

      const modalId = this.element.dataset.mobileInputModalId;
      if (!modalId) return;

      const modalElement = document.getElementById(modalId);
      if (!modalElement) return;
      const section = this.element.dataset.mobileInputModalSection;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.activateModalSection(modalElement, section);
      modalElement.dispatchEvent(new CustomEvent("modal:open"));
    };

    /**
     * Captura el click antes de `core-popup` (que escucha en bubbling)
     * para que en mobile SOLO abra la modal lateral.
     */
    this.element.addEventListener("click", this.clickHandler, {
      capture: true,
    });

    if (typeof window.matchMedia === "function") {
      this.mobileMq = window.matchMedia(MOBILE_MQ);
      this.mqHandler = () => this.syncReadonlyState();
      this.mobileMq.addEventListener("change", this.mqHandler);
    }

    this.syncReadonlyState();
  }

  destroy(): void {
    if (this.clickHandler) {
      this.element.removeEventListener("click", this.clickHandler, {
        capture: true,
      });
    }

    if (this.mobileMq && this.mqHandler) {
      this.mobileMq.removeEventListener("change", this.mqHandler);
    }

    if (this.innerInput) {
      this.innerInput.readOnly = false;
    }
  }

  private isMobile(): boolean {
    if (typeof window.matchMedia !== "function") return false;
    return window.matchMedia(MOBILE_MQ).matches;
  }

  private syncReadonlyState(): void {
    if (!this.innerInput) return;
    this.innerInput.readOnly = this.isMobile();
  }

  private activateModalSection(
    modalElement: HTMLElement,
    section?: string,
  ): void {
    if (!section) return;

    modalElement
      .querySelectorAll<HTMLElement>(FlySearchMobileInputModal.SECTION_SELECTOR)
      .forEach((node) => {
        const isActive = node.dataset.sectionId === section;
        node.dataset.sectionActive = String(isActive);
        node.style.display = isActive ? "flex" : "none";
      });

    modalElement
      .querySelectorAll<HTMLElement>(FlySearchMobileInputModal.FOOTER_SELECTOR)
      .forEach((node) => {
        const isActive = node.dataset.sectionId === section;
        node.dataset.sectionActive = String(isActive);
        node.style.display = isActive ? "flex" : "none";
      });

    const titleNode = modalElement.querySelector<HTMLElement>(
      FlySearchMobileInputModal.TITLE_SELECTOR,
    );
    const title = FlySearchMobileInputModal.sectionTitles[section];
    if (titleNode && title) {
      titleNode.textContent = title;
    }
  }
}

PluginRegistry.register(
  "fly-search-mobile-input-modal",
  FlySearchMobileInputModal,
);
